/**
 * Heading Skeleton Pattern
 *
 * A skeleton loader specifically designed for heading elements.
 * Provides appropriate sizing and styling for H1-H6 elements.
 */
import type { FC } from 'react';
import { Skeleton } from '../index';

export interface HeadingSkeletonProps {
	/**
	 * Heading level (1-6)
	 */
	level: 1 | 2 | 3 | 4 | 5 | 6;

	/**
	 * Width of the skeleton (CSS value)
	 * @default "60%" - Looks natural for heading text
	 */
	width?: string;

	/**
	 * Optional aria message for screen readers
	 */
	ariaMessage?: string;

	/**
	 * Custom class name
	 */
	className?: string;
}

/**
 * Heading skeleton component
 *
 * Renders a skeleton loader that matches the size and appearance of a heading element.
 * Height is automatically calculated based on heading level.
 */
export const HeadingSkeleton: FC<HeadingSkeletonProps> = ({
	level = 2,
	width = '60%',
	ariaMessage = 'Loading heading...',
	className = '',
}) => {
	// Font sizes that match typical heading sizes (in em units relative to base font)
	// These approximate the default WordPress heading sizes
	const headingSizes: Record<number, string> = {
		1: '2.5em', // h1
		2: '2em', // h2
		3: '1.75em', // h3
		4: '1.5em', // h4
		5: '1.25em', // h5
		6: '1em', // h6
	};

	const height = headingSizes[level] || headingSizes[2];

	return (
		<Skeleton
			width={width}
			height={height}
			ariaMessage={ariaMessage}
			className={className}
		/>
	);
};
